import type { Reference } from '@medplum/fhirtypes'
import type { Participant } from '@seen/core/models/patients/participant'
import { cn, typographyVariants } from '@seen/ui'
import Document from '@tiptap/extension-document'
import Paragraph from '@tiptap/extension-paragraph'
import Placeholder, {
  type PlaceholderOptions,
} from '@tiptap/extension-placeholder'
import Text from '@tiptap/extension-text'
import {
  type Editor,
  EditorContent,
  type EditorEvents,
  type Extension,
  type Extensions,
  type FocusPosition,
  type JSONContent,
  type UseEditorOptions,
  useEditor,
} from '@tiptap/react'
import { cva, type VariantProps } from 'class-variance-authority'
import {
  type JSX,
  type MutableRefObject,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import { tiptapVariants } from '#src/components/core/tiptapVariants.ts'
import { FloatingToolbar } from '#src/components/rich-text-editor/FloatingToolbar.tsx'
import {
  type EditorOutputFormat,
  getCleanedContent,
} from '#src/components/rich-text-editor/getCleanedContent.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useSyncEditorContent } from '#src/hooks/useSyncEditorContent.ts'
import { useTiptapExtensions } from '#src/hooks/useTiptapExtensions.ts'
import { parseNoteContent } from '#src/utils/noteContent.ts'

const variants = cva(
  cn(
    'relative w-full border-zinc-200 py-1.5 ring-offset-white focus-visible:outline-hidden focus-visible:ring-zinc-950 focus-visible:ring-offset-2 dark:border-zinc-800 dark:bg-zinc-950 dark:ring-offset-zinc-950 dark:focus-visible:ring-zinc-300'
  ),
  {
    variants: {
      variant: {
        default: 'rounded-md border px-3 focus-visible:ring-2',
        underline:
          'border-b text-sm hover:border-zinc-400 focus-visible:border-zinc-950 dark:focus-visible:border-zinc-300',
        h2: cn(
          typographyVariants({ variant: 'h2' }),
          'p-0 hover:bg-muted focus-visible:bg-muted [&_p]:leading-[inherit]'
        ),
        h3: cn(
          typographyVariants({ variant: 'h3' }),
          'p-0 hover:bg-muted focus-visible:bg-muted [&_p]:leading-[inherit]'
        ),
        h4: cn(
          typographyVariants({ variant: 'h4' }),
          'p-0 hover:bg-muted focus-visible:bg-muted [&_p]:leading-[inherit]'
        ),
        h5: cn(
          typographyVariants({ variant: 'h5' }),
          'p-0 hover:bg-muted focus-visible:bg-muted [&_p]:leading-[inherit]'
        ),
        h6: cn(
          typographyVariants({ variant: 'h6' }),
          'p-0 hover:bg-muted focus-visible:bg-muted [&_p]:leading-[inherit]'
        ),
        p: 'p-0 leading-7 hover:bg-muted focus-visible:bg-muted',
        large:
          'p-0 font-semibold text-lg hover:bg-muted focus-visible:bg-muted',
        lead: 'p-0 text-xl hover:bg-muted focus-visible:bg-muted',
        'p-ui': 'p-0 leading-6 hover:bg-muted focus-visible:bg-muted',
        unstyled: 'p-0 text-sm',
      },
      loading: {
        true: 'text-transparent transition-none after:absolute after:inset-0 after:z-10 after:animate-pulse after:rounded-md after:backdrop-brightness-loading',
        false: '',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-50 hover:border-zinc-200 hover:backdrop-brightness-100',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      loading: false,
      disabled: false,
    },
    compoundVariants: [
      {
        loading: true,
        variant: [
          'default',
          'underline',
          'h3',
          'h4',
          'lead',
          'p',
          'p-ui',
          'unstyled',
        ],
        className: [
          'border-transparent hover:border-transparent focus-visible:border-transparent',
        ],
      },
      {
        variant: 'unstyled',
        disabled: true,
        className:
          'cursor-default opacity-100 hover:border-zinc-200 hover:backdrop-brightness-100',
      },
    ],
  }
)

type HTML = string

export type RichTextEditorRef = {
  focus: () => void
  blur: () => void
}

// TODO: extend field
export interface RichTextEditorProps {
  defaultValue?: HTML // TODO: Replace defaultValue with value. value lets us spread {...field} directly without extracting value.
  value?: HTML | JSONContent | null
  placeholder?: PlaceholderOptions['placeholder']
  onChange?: (content: HTML) => any
  name?: string
  isTemplate?: boolean
  enableFileHandler?: boolean
  enableColumns?: boolean
  enablePractitionerMention?: boolean
  enableOrderTemplateMention?: boolean
  disableMention?: boolean
  variant?: VariantProps<typeof variants>['variant']
  editable?: boolean
  loading?: boolean
  disabled?: boolean
  onFocus?: (event: FocusEvent) => void
  onBlur?: (event: FocusEvent) => void
  autofocus?: FocusPosition
  choiceNodesEditable?: boolean
  editorRef?: MutableRefObject<Editor | null>
  contentClassName?: string
  className?: string
  participant?: Participant
  related?: Reference[]
  'aria-label'?: string
  simpleText?: boolean
  ref?: Ref<RichTextEditorRef>
  /* Appended to the built-in extension list at editor construction, and only
   * there — TipTap routes a later array to `setOptions()`, which never rebuilds
   * the extension manager, so a new identity is silently ignored rather than
   * remounting. Keep it stable; have extensions read late-arriving values
   * through refs or transaction meta. */
  additionalExtensions?: Extensions
  /* Serialization format for onChange. 'html' (default): emits DOMPurify-
   * sanitized HTML. 'json': emits JSON.stringify(editor.getJSON()). The JSON
   * path skips DOMPurify because JSON isn't XSS-rendered directly — readers
   * convert back to HTML via RENDER_EXTENSIONS, whose nodes own their own
   * serialization. */
  outputFormat?: EditorOutputFormat
}

export function RichTextEditor({
  editable = true,
  loading,
  disabled,
  ...rest
}: RichTextEditorProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const isDisabled = disabled || isReadOnly
  const isEditable = !loading && !isDisabled && !!editable
  return (
    <RichTextEditorInner
      key={`${isEditable}-${rest.enableFileHandler !== false}`} // There's a bug where if editable changes, the extensions (i.e. ChoiceNode) don't update accordingly, so we force a re-initialization of the editor.
      editable={editable}
      loading={loading}
      disabled={isDisabled}
      {...rest}
    />
  )
}

function RichTextEditorInner({
  defaultValue,
  value,
  onChange,
  contentClassName,
  loading,
  variant,
  name,
  disabled,
  isTemplate,
  enableColumns,
  enableFileHandler,
  enablePractitionerMention,
  enableOrderTemplateMention,
  disableMention,
  editable = true,
  placeholder,
  onBlur,
  onFocus,
  autofocus = false,
  choiceNodesEditable = false,
  editorRef,
  className,
  participant,
  related,
  'aria-label': ariaLabel,
  simpleText = false,
  ref,
  additionalExtensions,
  outputFormat = 'html',
}: RichTextEditorProps): JSX.Element {
  const rawContent = value ?? defaultValue ?? ''
  // parseNoteContent returns a fresh object for JSON-doc strings; memoize so
  // content identity stays stable across renders. useSyncEditorContent keys its
  // effect on it, and with shouldRerenderOnTransaction every transaction
  // re-renders — an unstable identity re-arms the effect each time.
  const content = useMemo(
    () =>
      typeof rawContent === 'string'
        ? parseNoteContent(rawContent)
        : rawContent,
    [rawContent]
  )

  const isEditable = !loading && !disabled && !!editable

  const handleFocus: UseEditorOptions['onFocus'] = useCallback(
    (props: EditorEvents['focus']) => {
      onFocus?.(props.event)
    },
    [onFocus]
  )

  const handleBlur: UseEditorOptions['onBlur'] = useCallback(
    (props: EditorEvents['blur']) => {
      onBlur?.(props.event)
    },
    [onBlur]
  )

  const { extensions: defaultExtensions, cancelObservationTracking } =
    useTiptapExtensions({
      placeholder,
      enableFileHandler,
      fileHandlerDocumentReferenceContextRelated: related,
      contextParticipant: participant,
      choiceNodesEditable,
      enablePractitionerMention,
      enableOrderTemplateMention,
      disableMention,
      isTemplate,
      enableColumns,
    })

  const extensions = useMemo(
    () => [...defaultExtensions, ...(additionalExtensions ?? [])],
    [defaultExtensions, additionalExtensions]
  )

  /* Same one-shot registration as `CollaborativeEditor` — see the
   * `additionalExtensions` comment there for why `useEditor` ignores a later
   * array rather than remounting. This editor renders the Elation note body,
   * so a silently dropped extension takes half of a feature with it. */
  const initialAdditionalExtensions = useRef(additionalExtensions)
  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      additionalExtensions !== initialAdditionalExtensions.current
    ) {
      console.warn(
        '[RichTextEditor] `additionalExtensions` changed identity after mount. ' +
          'TipTap registers extensions once, at editor construction, so the new ' +
          'ones are ignored and any ProseMirror plugin they add will never run.'
      )
    }
  }, [additionalExtensions])

  const options = useMemo(
    () =>
      ({
        // Defer view mount to after first commit so consumers reading editor.view don't fire before it's attached.
        immediatelyRender: false,
        // Re-render on every transaction so toolbar / selection-driven UI stays in sync with editor state.
        shouldRerenderOnTransaction: true,
        extensions: simpleText
          ? [
              Document,
              Text,
              Paragraph,
              Placeholder.configure({
                placeholder,
              }) as Extension<PlaceholderOptions>,
            ]
          : extensions,
        autofocus,
        content,
        parseOptions: {
          // ##PreserveWhitespace Without this, spaces between adjacent inline nodes (e.g., "BP _____ HR _____") collapse during HTML parsing.
          // Repro: load a template with multiple observation nodes on one line and observe them merging together.
          preserveWhitespace: true,
        },
        editorProps: {
          attributes: {
            class: cn(
              tiptapVariants(),
              variants({
                variant,
                loading,
                disabled,
                className: contentClassName,
              })
            ),
          },
          handleDOMEvents: {
            keydown: (_, event) => {
              // Disable meta+enter. We often use it for form submission.
              if (event.metaKey && event.key === 'Enter') {
                return true
              }
              return false
            },
            click: (_, event) => {
              // Prevent the click from bubbling up parent elements
              event.stopPropagation()
              return false
            },
          },
        },
        onUpdate: ({ editor, transaction, appendedTransactions }) => {
          /* Persist genuine edits only. A doc change contributed entirely by
           * plugin-appended transactions, on top of an originating transaction
           * that changed nothing, is the editor normalizing itself rather than
           * the user editing. StarterKit's TrailingNode is the concrete case:
           * whenever the doc ends in a non-paragraph node (list, heading,
           * blockquote) it appends an empty trailing paragraph on the first
           * transaction after mount, and the transaction TipTap dispatches on
           * focus is normally that first one. getCleanedContent then strips
           * that paragraph back off, so the autosave wrote a value the user
           * never authored — restamping author/timestamp and adding a version
           * to history for someone who only clicked into the editor.
           * Typing, pasting, drag-drop and setContent all change the doc in the
           * originating transaction, so no persistable edit is dropped here. */
          if (!transaction.docChanged && appendedTransactions.length > 0) {
            return
          }
          onChange?.(getCleanedContent(editor, outputFormat))
        },
        onFocus: handleFocus,
        onBlur: handleBlur,
        editable: isEditable,
        // enableContentCheck: true,
        // onContentError: handleTiptapContentError,
      }) satisfies UseEditorOptions,
    [
      simpleText,
      content,
      onChange,
      disabled,
      loading,
      isEditable,
      placeholder,
      autofocus,
      handleFocus,
      handleBlur,
      contentClassName,
      variant,
      extensions,
      outputFormat,
    ]
  )

  const editor = useEditor(options)
  if (editorRef) {
    editorRef.current = editor
  }

  useSyncEditorContent(editor, content, cancelObservationTracking)

  useImperativeHandle(ref, () => ({
    focus() {
      editor?.commands.focus()
    },
    blur() {
      editor?.commands.blur()
    },
  }))

  return (
    <div className={className}>
      <EditorContent name={name} editor={editor} aria-label={ariaLabel} />
      {!simpleText && (
        <FloatingToolbar editor={editor} enableColumns={enableColumns} />
      )}
    </div>
  )
}
